import {
  Component,
  OnInit,
  inject,
  signal,
  computed,
  effect,
  ElementRef,
  HostListener,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { finalize } from 'rxjs';
import { Promotion, PromotionStatus } from '../../shared/interfaces';
import { PromotionsService } from '../../services/promotions/promotions.service';
import { AuthService } from '../../services/auth';
import { PageTitleService, ToastService } from '../../shared/services';
import { Modal } from '../../shared/components/modal/modal';
import { ConfirmModal } from '../../shared/components/confirm-modal/confirm-modal';
import { PromotionAdd } from './promotion-add/promotion-add';

@Component({
  selector: 'app-promos',
  imports: [CommonModule, Modal, ConfirmModal],
  templateUrl: './promos.html',
  styleUrl: './promos.css',
})
export class Promos implements OnInit {
  private readonly promotionsService = inject(PromotionsService);
  private readonly authService = inject(AuthService);
  private readonly toastService = inject(ToastService);
  private readonly titleService = inject(PageTitleService);

  readonly promotions = signal<Promotion[]>([]);
  readonly activeFilter = signal<PromotionStatus>('active');
  readonly selectedPromotion = signal<Promotion | null>(null);
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly modalOpen = signal(false);
  private readonly editingPromotionId = signal<string | null>(null);
  readonly isEditMode = computed(() => this.editingPromotionId() !== null);

  PromotionAddForm = PromotionAdd;
  modalBodyInputs: Record<string, unknown> = {};
  confirmDeleteOpen = false;

  private readonly detailCloseBtn = viewChild<ElementRef<HTMLButtonElement>>('detailCloseBtn');
  private returnFocusTo: HTMLElement | null = null;

  private readonly currencyFormatter = new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  readonly emptyState = computed(() => {
    if (this.promotions().length === 0) {
      return {
        title: 'Aún no tienes promociones',
        text: 'Crea un descuento para atraer clientes nuevos o premiar a los de siempre.',
      };
    }
    switch (this.activeFilter()) {
      case 'active':
        return { title: 'No tienes promociones activas', text: 'Activa una programada o crea una nueva.' };
      case 'scheduled':
        return { title: 'No hay promociones programadas', text: 'Las promociones con fecha de inicio futura aparecen aquí.' };
      case 'draft':
        return { title: 'No tienes borradores', text: 'Las promociones que aún no publicas aparecen aquí.' };
      default:
        return { title: 'Ninguna promoción ha terminado', text: 'Las promociones que finalizan aparecen aquí.' };
    }
  });

  constructor() {
    // Move focus into the detail panel when it opens, and back when it closes
    effect(() => {
      const open = !!this.selectedPromotion();
      const btn = this.detailCloseBtn();
      if (open && btn) {
        btn.nativeElement.focus();
      } else if (!open && this.returnFocusTo) {
        this.returnFocusTo.focus?.();
        this.returnFocusTo = null;
      }
    });
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.selectedPromotion() && !this.modalOpen() && !this.confirmDeleteOpen) {
      this.selectedPromotion.set(null);
    }
  }

  readonly stats = computed(() => {
    const list = this.promotions();
    return {
      total: list.length,
      active: list.filter((p) => p.status === 'active').length,
      scheduled: list.filter((p) => p.status === 'scheduled').length,
      expired: list.filter((p) => p.status === 'expired').length,
    };
  });

  readonly promotionsByStatus = computed(() => {
    const grouped: Record<PromotionStatus, Promotion[]> = {
      active: [],
      scheduled: [],
      draft: [],
      expired: [],
    };
    for (const promo of this.promotions()) {
      grouped[promo.status]?.push(promo);
    }
    return grouped;
  });

  readonly filteredList = computed(() =>
    this.promotionsByStatus()[this.activeFilter()] ?? []
  );

  readonly statusSections: { status: PromotionStatus; label: string }[] = [
    { status: 'active', label: 'Activas' },
    { status: 'scheduled', label: 'Programadas' },
    { status: 'draft', label: 'Borradores' },
    { status: 'expired', label: 'Finalizadas' },
  ];

  ngOnInit() {
    this.titleService.setPage({
      title: 'Promociones',
      subtitle: 'Descuentos y campañas para atraer y traer de vuelta a tus clientes',
    });
    this.loadPromotions();
  }

  selectPromotion(promo: Promotion) {
    this.returnFocusTo = document.activeElement as HTMLElement | null;
    this.selectedPromotion.set(promo);
  }

  openCreateModal() {
    this.editingPromotionId.set(null);
    this.modalBodyInputs = { entityId: null };
    this.modalOpen.set(true);
  }

  openEditModal() {
    const promo = this.selectedPromotion();
    if (!promo) return;
    this.editingPromotionId.set(promo.id);
    this.modalBodyInputs = { entityId: promo.id };
    this.modalOpen.set(true);
  }

  closeModal() {
    this.modalOpen.set(false);
    this.editingPromotionId.set(null);
  }

  onModalConfirm(payload: any): void {
    if (!payload) return;
    const businessId = this.requireBusinessId();
    this.saving.set(true);

    if (payload.promotionId) {
      this.promotionsService
        .updatePromotion(businessId, payload.promotionId, payload)
        .pipe(finalize(() => this.saving.set(false)))
        .subscribe({
          next: () => {
            this.toastService.show('Promoción actualizada', 'success');
            this.closeModal();
            this.loadPromotions(payload.promotionId);
          },
          error: (err: any) => {
            this.toastService.show(
              err?.error?.message || err?.message || 'No se pudo actualizar la promoción. Inténtalo de nuevo.',
              'warning'
            );
          },
        });
    } else {
      this.promotionsService
        .createPromotion(businessId, payload)
        .pipe(finalize(() => this.saving.set(false)))
        .subscribe({
          next: (res: { id: string }) => {
            this.toastService.show('Promoción creada', 'success');
            this.closeModal();
            this.loadPromotions(res?.id);
          },
          error: (err: any) => {
            this.toastService.show(
              err?.error?.message || err?.message || 'No se pudo crear la promoción. Inténtalo de nuevo.',
              'warning'
            );
          },
        });
    }
  }

  setStatus(promo: Promotion, status: PromotionStatus) {
    if (promo.status === status) return;
    const businessId = this.requireBusinessId();
    this.promotionsService.updateStatus(businessId, promo.id, status).subscribe({
      next: () => {
        this.toastService.show('Estado actualizado', 'success');
        this.loadPromotions(promo.id);
      },
      error: (err: any) => {
        this.toastService.show(
          err?.message || 'No se pudo cambiar el estado. Inténtalo de nuevo.',
          'warning'
        );
      },
    });
  }

  deletePromotion() {
    if (!this.selectedPromotion()) return;
    this.confirmDeleteOpen = true;
  }

  onConfirmDelete() {
    const promo = this.selectedPromotion();
    if (!promo) return;
    const businessId = this.requireBusinessId();
    this.promotionsService.deletePromotion(businessId, promo.id).subscribe({
      next: () => {
        this.toastService.show('Promoción eliminada', 'success');
        this.selectedPromotion.set(null);
        this.loadPromotions();
      },
      error: (err: any) => {
        this.toastService.show(
          err?.message || 'No se pudo eliminar la promoción. Inténtalo de nuevo.',
          'warning'
        );
      },
    });
  }

  goalProgress(promo: Promotion | null) {
    if (!promo || !promo.goal || promo.goal === 0) return 0;
    return Math.min(100, Math.round((promo.redemptions / promo.goal) * 100));
  }

  statusLabel(status: PromotionStatus) {
    switch (status) {
      case 'active':
        return 'Activa';
      case 'scheduled':
        return 'Programada';
      case 'expired':
        return 'Finalizada';
      default:
        return 'Borrador';
    }
  }

  audienceLabel(audience?: Promotion['audience']) {
    switch (audience) {
      case 'vipClients':
        return 'Clientes VIP';
      case 'newClients':
        return 'Clientes nuevos';
      case 'allClients':
        return 'Todos los clientes';
      default:
        return 'Segmento no definido';
    }
  }

  channelLabel(channel: Promotion['channels'][number]) {
    switch (channel) {
      case 'whatsapp':
        return 'WhatsApp';
      case 'in_app':
        return 'Portal público';
      default:
        return 'Email';
    }
  }

  discountValue(promo: Promotion): string {
    return promo.discountType === 'percentage'
      ? `${promo.value} %`
      : this.currencyFormatter.format(promo.value);
  }

  discountText(promo: Promotion): string {
    return promo.discountType === 'percentage'
      ? `${promo.value} % de descuento`
      : `${this.currencyFormatter.format(promo.value)} de descuento`;
  }

  servicesLabel(promo: Promotion): string {
    const services = promo.services ?? [];
    if (services.length === 0) return 'En todos los servicios';
    if (services.length <= 2) return `En ${services.join(' y ')}`;
    return `En ${services[0]} y ${services.length - 1} servicios más`;
  }

  channelsText(promo: Promotion): string {
    const labels = promo.channels.map((c) => this.channelLabel(c));
    return labels.length > 1
      ? `${labels.slice(0, -1).join(', ')} y ${labels[labels.length - 1]}`
      : labels[0];
  }

  /** Human description of where the promotion is in its date range, plus elapsed % */
  timeInfo(promo: Promotion): { text: string; percent: number } {
    const day = 24 * 60 * 60 * 1000;
    const now = Date.now();
    const start = new Date(promo.startDate).getTime();
    const end = new Date(promo.endDate).getTime();
    const fmt = (t: number) =>
      new Date(t).toLocaleDateString('es-CO', { day: 'numeric', month: 'short' });
    if (isNaN(start) || isNaN(end)) return { text: '', percent: 0 };

    if (now < start) {
      const days = Math.ceil((start - now) / day);
      return { text: days <= 1 ? 'Empieza mañana' : `Empieza en ${days} días, el ${fmt(start)}`, percent: 0 };
    }
    if (now > end) {
      return { text: `Terminó el ${fmt(end)}`, percent: 100 };
    }
    const daysLeft = Math.ceil((end - now) / day);
    const percent = Math.round(((now - start) / Math.max(end - start, 1)) * 100);
    return {
      text: daysLeft <= 1 ? 'Termina hoy' : `Quedan ${daysLeft} días, hasta el ${fmt(end)}`,
      percent,
    };
  }

  getPromosByStatus(status: PromotionStatus): Promotion[] {
    return this.promotionsByStatus()[status] ?? [];
  }

  private loadPromotions(selectId?: string) {
    const businessId = this.requireBusinessId();
    this.loading.set(true);
    this.promotionsService
      .listPromotions(businessId)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (data) => {
          const normalized = data.map((p) => this.normalizePromotion(p));
          this.promotions.set(normalized);
          this.reconcileSelection(selectId);
        },
        error: (err) => {
          this.toastService.show(
            err?.message || 'No se pudieron cargar las promociones. Recarga la página.',
            'warning'
          );
        },
      });
  }

  private reconcileSelection(preferredId?: string | null) {
    const list = this.promotions();
    if (!preferredId) return;
    const match = list.find((p) => p.id === preferredId);
    if (match) this.selectedPromotion.set(match);
  }

  private requireBusinessId(): string {
    const id = this.authService.getUserInfo()?.business_id;
    if (!id) {
      this.toastService.show(
        'No encontramos tu negocio. Cierra sesión y vuelve a entrar.',
        'warning'
      );
      throw new Error('No se pudo resolver el negocio de la sesion actual.');
    }
    return id;
  }

  private normalizePromotion(promo: Promotion): Promotion {
    return {
      ...promo,
      status: this.normalizeStatus(promo.status),
      audience: this.normalizeAudience(promo.audience),
      discountType: this.normalizeDiscountType(promo.discountType),
      channels: promo.channels ?? [],
    };
  }

  private normalizeStatus(value: PromotionStatus | string): PromotionStatus {
    switch (value?.toString().toLowerCase()) {
      case 'active':
        return 'active';
      case 'scheduled':
        return 'scheduled';
      case 'expired':
        return 'expired';
      default:
        return 'draft';
    }
  }

  private normalizeAudience(value: Promotion['audience'] | string) {
    switch (value?.toString().toLowerCase()) {
      case 'vipclients':
      case 'vip_clients':
        return 'vipClients';
      case 'newclients':
      case 'new_clients':
        return 'newClients';
      default:
        return 'allClients';
    }
  }

  private normalizeDiscountType(
    value: Promotion['discountType'] | string
  ): 'percentage' | 'fixed' {
    switch (value?.toString().toLowerCase()) {
      case 'fixed':
      case 'fixed_amount':
      case 'amount':
        return 'fixed';
      default:
        return 'percentage';
    }
  }
}
